/**
 * Chrono-lite. Deterministic parser for the natural-date phrasings that
 * show up in planning prompts. We resolve to ISO dates and human-friendly
 * labels so Line can quote them verbatim ("Scheduling for Fri Aug 8")
 * instead of asking "which Friday did you mean?".
 *
 * The parser is intentionally conservative: it prefers returning nothing
 * over guessing. Every returned date carries a `sourceText` so the caller
 * can render implicit-confirmation copy the user can correct in one edit.
 */

export interface ParsedDate {
  iso: string
  label: string
  sourceText: string
}

const WEEKDAY_INDEX: Record<string, number> = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
}

const MONTHS = [
  ['january', 'jan'], ['february', 'feb'], ['march', 'mar'], ['april', 'apr'],
  ['may'], ['june', 'jun'], ['july', 'jul'], ['august', 'aug'],
  ['september', 'sep', 'sept'], ['october', 'oct'], ['november', 'nov'], ['december', 'dec'],
]

const WEEKDAY_LABEL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_LABEL_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const startOfDay = (date: Date): Date => {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

const addDays = (date: Date, days: number): Date => {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

const toIso = (date: Date): string => {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const formatLabel = (date: Date): string =>
  `${WEEKDAY_LABEL[date.getDay()].slice(0, 3)} ${MONTH_LABEL_SHORT[date.getMonth()]} ${date.getDate()}`

const parseMonthName = (name: string): number => {
  const lower = name.toLowerCase()
  for (let i = 0; i < MONTHS.length; i += 1) {
    if (MONTHS[i].includes(lower)) return i
  }
  return -1
}

const nextWeekday = (from: Date, targetDow: number, forceNextWeek = false): Date => {
  const anchor = startOfDay(from)
  const diff = (targetDow - anchor.getDay() + 7) % 7 || 7
  const bump = forceNextWeek && diff <= 7 ? diff + 7 : diff
  return addDays(anchor, bump)
}

const emit = (sourceText: string, date: Date): ParsedDate => ({
  iso: toIso(date),
  label: formatLabel(date),
  sourceText,
})

/**
 * Parse the first plausible future date reference in the input.
 * Returns null when nothing recognizable is found.
 *
 * `now` is injectable to keep tests hermetic.
 */
export const parseNaturalDate = (input: string, now: Date = new Date()): ParsedDate | null => {
  if (!input) return null
  const text = input.trim()
  if (!text) return null
  const today = startOfDay(now)
  const lower = text.toLowerCase()

  // Explicit ISO date wins outright.
  const isoMatch = /\b(\d{4})-(\d{2})-(\d{2})\b/.exec(text)
  if (isoMatch) {
    const y = Number(isoMatch[1])
    const m = Number(isoMatch[2]) - 1
    const d = Number(isoMatch[3])
    const date = new Date(y, m, d)
    if (!Number.isNaN(date.getTime())) return emit(isoMatch[0], date)
  }

  // "today" / "tomorrow" / "day after tomorrow" / "yesterday"
  if (/\b(today)\b/i.test(text)) return emit('today', today)
  if (/\b(day\s+after\s+tomorrow)\b/i.test(text)) return emit('day after tomorrow', addDays(today, 2))
  if (/\b(tomorrow)\b/i.test(text)) return emit('tomorrow', addDays(today, 1))
  if (/\b(yesterday)\b/i.test(text)) return emit('yesterday', addDays(today, -1))

  // "in N days/weeks/months"
  const relMatch = /\b(?:in\s+)?(\d+)\s+(day|week|month)s?\b/i.exec(text)
  if (relMatch && /\bin\b/i.test(text)) {
    const value = Number(relMatch[1])
    const unit = relMatch[2].toLowerCase()
    const days = unit === 'day' ? value : unit === 'week' ? value * 7 : value * 30
    return emit(`in ${value} ${unit}${value === 1 ? '' : 's'}`, addDays(today, days))
  }

  // "next Monday" / "next week" / "next month"
  const nextMatch = /\bnext\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat|week|month)\b/i.exec(text)
  if (nextMatch) {
    const token = nextMatch[1].toLowerCase()
    if (token === 'week') return emit('next week', addDays(today, 7))
    if (token === 'month') {
      const nm = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      return emit('next month', nm)
    }
    const dow = WEEKDAY_INDEX[token]
    if (typeof dow === 'number') return emit(`next ${WEEKDAY_LABEL[dow]}`, nextWeekday(today, dow, false))
  }

  // "by Friday" / "by August 8" / "by end of month"
  const byMatch = /\bby\s+(?:the\s+)?(end\s+of\s+(?:this\s+)?(?:week|month|quarter|year)|next\s+\w+|(?:sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)|(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2}))\b/i.exec(text)
  if (byMatch) {
    const phrase = byMatch[1].toLowerCase()
    if (phrase.startsWith('end of')) {
      if (phrase.includes('week')) {
        const sunday = nextWeekday(today, 0, false) // next Sunday
        return emit(phrase, sunday)
      }
      if (phrase.includes('month')) {
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        return emit(phrase, lastDay)
      }
      if (phrase.includes('quarter')) {
        const q = Math.floor(today.getMonth() / 3)
        const lastMonth = q * 3 + 2
        const lastDay = new Date(today.getFullYear(), lastMonth + 1, 0)
        return emit(phrase, lastDay)
      }
      if (phrase.includes('year')) return emit(phrase, new Date(today.getFullYear(), 11, 31))
    }
    if (phrase.startsWith('next ')) {
      const nested = parseNaturalDate(`next ${phrase.slice(5)}`, now)
      if (nested) return emit(`by ${nested.sourceText}`, new Date(`${nested.iso}T00:00:00`))
    }
    const dow = WEEKDAY_INDEX[phrase]
    if (typeof dow === 'number') {
      return emit(`by ${WEEKDAY_LABEL[dow]}`, nextWeekday(today, dow, false))
    }
    // "by August 8"
    const monthIdx = parseMonthName(phrase)
    if (monthIdx >= 0 && byMatch[2]) {
      const day = Number(byMatch[2])
      const yearGuess = today.getFullYear()
      let date = new Date(yearGuess, monthIdx, day)
      if (date.getTime() < today.getTime()) date = new Date(yearGuess + 1, monthIdx, day)
      return emit(`${byMatch[1]}`.trim(), date)
    }
  }

  // "August 8" without "by"
  const monthDayMatch = /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?\b/i.exec(text)
  if (monthDayMatch) {
    const monthIdx = parseMonthName(monthDayMatch[1])
    if (monthIdx >= 0) {
      const day = Number(monthDayMatch[2])
      const yearGuess = today.getFullYear()
      let date = new Date(yearGuess, monthIdx, day)
      if (date.getTime() < today.getTime() - 30 * 24 * 60 * 60 * 1000) {
        date = new Date(yearGuess + 1, monthIdx, day)
      }
      return emit(monthDayMatch[0], date)
    }
  }

  // Bare weekday name (e.g. "Friday" — resolves to nearest future Friday)
  const bareDowMatch = /\b(this\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i.exec(lower)
  if (bareDowMatch) {
    const dow = WEEKDAY_INDEX[bareDowMatch[2]]
    if (typeof dow === 'number') {
      return emit(bareDowMatch[0].trim(), nextWeekday(today, dow, false))
    }
  }

  return null
}

/**
 * Render a "PARSED DATES" line for the system prompt so the model can
 * quote the resolved dates verbatim (implicit confirmation pattern).
 */
export const renderParsedDatesForPrompt = (parsed: ParsedDate | null | undefined): string => {
  if (!parsed) return ''
  return `PARSED DATES: user said "${parsed.sourceText}" → resolves to ${parsed.label} (${parsed.iso}). Quote the resolved date in your reply so the user can correct it in one edit.`
}
